/**
 * CardAssetPanel — admin pipeline UI: upload a card image → rembg → GPT-5
 * Vision → review extracted metadata → save.
 *
 * Renders inside `AdminCardEditor` as a dedicated "Image & metadata" tab.
 *
 * Workflow
 *   1. Drop / pick a PNG / JPG / WebP. Max 12 MB. Shown in a preview tile.
 *   2. Click "Upload & process" → multipart POST to
 *      `/api/admin/cards/{cardId}/asset`. Pipeline runs serverside.
 *   3. On success, the panel renders the THREE processed variants
 *      (thumb, preview, processed) using `<CardImage variant=... />` and
 *      the GPT-5 metadata envelope.
 *   4. Operator can:
 *        • "Reprocess" → re-run rembg + Vision against the stored raw.
 *        • "Replace image" → triggers the file picker again.
 *        • "Remove image" → soft-delete (clears the envelope on the card).
 *
 * Notes
 *   • `bust` is bumped after every successful op so the rendered <img> tags
 *     don't show stale cached bytes.
 *   • The component does not optimistically modify card_image state in
 *     the parent — it always re-derives from the server-returned envelope.
 */
import { useCallback, useRef, useState } from 'react';
import {
  Upload, ImageIcon, Loader2, RefreshCcw, Sparkles, Trash2, CheckCircle2,
  AlertCircle, FileImage,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { CardImage } from '@/components/brand/CardImage';
import { AdminAPI } from '@/lib/admin-api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const MAX_BYTES = 12 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';

const MetadataRow = ({ label, value, mono = false }) => (
  <div className="flex items-baseline justify-between gap-3 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className={cn('text-foreground/95', mono && 'font-mono-num')}>
      {value === null || value === undefined || value === '' ? <span className="text-muted-foreground italic">—</span> : String(value)}
    </span>
  </div>
);

const Swatch = ({ hex, label }) => (
  <div className="flex flex-col items-center gap-1" data-testid={`card-asset-swatch-${label.toLowerCase()}`}>
    <div
      className="h-9 w-9 rounded-md border border-border shrink-0"
      style={{ background: hex || 'transparent' }}
      title={hex || '—'}
    />
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="text-[10px] font-mono-num text-foreground/85">{hex || '—'}</span>
  </div>
);

export const CardAssetPanel = ({ cardId, bank, name, image, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState('');
  // Phase 12.1 — UX choices the admin makes at upload time. Defaults are
  // tuned for "user uploaded a photo": auto-detect orientation, enhance
  // (rembg+vision) because phone photos usually have busy backgrounds.
  // For clean official bank images the admin should switch to "As-is".
  const [processingMode, setProcessingMode] = useState('enhanced');  // 'enhanced' | 'as_is' | 'auto'
  const [orientation, setOrientation] = useState('auto');             // 'horizontal' | 'vertical' | 'auto'
  const [skipVision, setSkipVision] = useState(false);
  const [pending, setPending] = useState(null);            // File picked but not uploaded
  const [bust, setBust] = useState(image?.uploaded_at || 0);
  const hasImage = !!(image && !image.is_deleted);
  const metadata = image?.metadata || {};
  const isVerticalCard = !!metadata?.features?.is_vertical;

  const handlePick = () => fileRef.current?.click();

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      toast.error(`Unsupported file type: ${file.type || 'unknown'}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 12 MB.`);
      return;
    }
    setPending(file);
  }, []);

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';   // allow re-picking the same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const startUpload = async () => {
    if (!pending) return;
    setBusy(true);
    setBusyKind('upload');
    try {
      const res = await AdminAPI.cardAssets.upload(cardId, pending, {
        skipVision, processingMode, orientation,
      });
      setPending(null);
      setBust(Date.now());
      onChange?.(res.image);
      toast.success('Image processed and saved.');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
      setBusyKind('');
    }
  };

  const reprocess = async () => {
    setBusy(true);
    setBusyKind('reprocess');
    try {
      const res = await AdminAPI.cardAssets.reprocess(cardId, {
        skipVision, processingMode, orientation,
      });
      setBust(Date.now());
      onChange?.(res.image);
      toast.success('Re-processed against the original upload.');
    } catch (e) {
      toast.error(e.message || 'Reprocess failed');
    } finally {
      setBusy(false);
      setBusyKind('');
    }
  };

  const removeImage = async () => {
    if (!window.confirm('Remove this card image? The original upload is retained in storage and can be restored via reprocess.')) return;
    setBusy(true);
    setBusyKind('delete');
    try {
      await AdminAPI.cardAssets.remove(cardId);
      onChange?.(null);
      setBust(Date.now());
      toast.success('Image removed.');
    } catch (e) {
      toast.error(e.message || 'Remove failed');
    } finally {
      setBusy(false);
      setBusyKind('');
    }
  };

  return (
    <div className="space-y-5" data-testid="card-asset-panel">
      {/* Top: live preview tiles for the three processed variants. */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Thumbnail (256px)</Label>
            <CardImage cardId={cardId} bank={bank} name={name} variant="thumb"     size="lg"  bust={bust} hasImage={hasImage} isVertical={isVerticalCard} asTile />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preview (800px)</Label>
            <CardImage cardId={cardId} bank={bank} name={name} variant="preview"   size="2xl" bust={bust} hasImage={hasImage} isVertical={isVerticalCard} asTile />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Processed (full)</Label>
            <CardImage cardId={cardId} bank={bank} name={name} variant="processed" size="xl"  bust={bust} hasImage={hasImage} isVertical={isVerticalCard} asTile />
          </div>
        </div>
        {!hasImage && (
          <p className="mt-3 text-xs text-muted-foreground italic flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> No image yet — upload one below to power consumer + ranking views.
          </p>
        )}
      </Card>

      {/* Middle: dropzone + actions */}
      <Card className="p-4">
        {/* Phase 12.1 — processing-mode and orientation pickers above the
            dropzone. Defaults: 'enhanced' + 'auto'. */}
        <div className="grid sm:grid-cols-2 gap-4 mb-4 pb-4 border-b border-border">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Processing</Label>
            <RadioGroup
              value={processingMode}
              onValueChange={setProcessingMode}
              className="grid gap-1.5"
              data-testid="card-asset-processing-mode"
            >
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                processingMode === 'enhanced' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="enhanced" data-testid="card-asset-mode-enhanced" />
                <div className="flex-1">
                  <div className="font-medium">AI enhanced <span className="text-[10px] text-muted-foreground font-normal">(default)</span></div>
                  <div className="text-[11px] text-muted-foreground">Runs rembg to remove the background. Best for phone photos or images with busy backgrounds.</div>
                </div>
              </label>
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                processingMode === 'as_is' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="as_is" data-testid="card-asset-mode-as-is" />
                <div className="flex-1">
                  <div className="font-medium">Upload as-is</div>
                  <div className="text-[11px] text-muted-foreground">Keep the source image untouched. Pick this for clean official bank images where rembg would degrade quality.</div>
                </div>
              </label>
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                processingMode === 'auto' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="auto" data-testid="card-asset-mode-auto" />
                <div className="flex-1">
                  <div className="font-medium">Auto-detect</div>
                  <div className="text-[11px] text-muted-foreground">Server picks: clean borders → as-is; busy backgrounds → enhanced.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Orientation</Label>
            <RadioGroup
              value={orientation}
              onValueChange={setOrientation}
              className="grid gap-1.5"
              data-testid="card-asset-orientation"
            >
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                orientation === 'auto' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="auto" data-testid="card-asset-orient-auto" />
                <div className="flex-1">
                  <div className="font-medium">Auto-detect <span className="text-[10px] text-muted-foreground font-normal">(default)</span></div>
                  <div className="text-[11px] text-muted-foreground">Pick portrait or landscape from the image's actual aspect ratio.</div>
                </div>
              </label>
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                orientation === 'horizontal' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="horizontal" data-testid="card-asset-orient-h" />
                <div className="flex-1">
                  <div className="font-medium">Horizontal</div>
                  <div className="text-[11px] text-muted-foreground">Standard landscape card (1.586:1).</div>
                </div>
              </label>
              <label className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors',
                orientation === 'vertical' ? 'border-accent/50 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]',
              )}>
                <RadioGroupItem value="vertical" data-testid="card-asset-orient-v" />
                <div className="flex-1">
                  <div className="font-medium">Vertical</div>
                  <div className="text-[11px] text-muted-foreground">Portrait card (1:1.586). Common on Magnus, Atlas, AmEx Centurion designs.</div>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={onInputChange}
          data-testid="card-asset-file-input"
        />
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={handlePick}
          className={cn(
            'relative grid place-items-center rounded-xl border-2 border-dashed p-6 cursor-pointer transition-colors',
            'border-border hover:border-accent/50 hover:bg-[hsl(var(--surface-2))]',
            pending && 'border-accent/60 bg-accent/5',
          )}
          data-testid="card-asset-dropzone"
        >
          {pending ? (
            <div className="flex items-center gap-3 text-sm">
              <FileImage className="h-5 w-5 text-accent" />
              <span className="text-foreground">{pending.name}</span>
              <span className="text-muted-foreground">· {(pending.size / 1024).toFixed(0)} KB</span>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="h-7 w-7 mx-auto text-muted-foreground/70" />
              <div className="mt-2 text-sm">
                <span className="font-medium">Click to choose</span>{' '}<span className="text-muted-foreground">or drag a card image here</span>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">PNG, JPG, or WebP · up to 12 MB</div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs">
            <Switch
              id="card-asset-skip-vision"
              checked={skipVision}
              onCheckedChange={setSkipVision}
              data-testid="card-asset-skip-vision-toggle"
            />
            <Label htmlFor="card-asset-skip-vision" className="text-xs text-muted-foreground select-none cursor-pointer">
              Skip Vision metadata (faster, no GPT-5 call)
            </Label>
          </div>
          <div className="flex items-center gap-2">
            {hasImage && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeImage}
                  disabled={busy}
                  className="gap-1.5 text-muted-foreground hover:text-rose-500"
                  data-testid="card-asset-remove-button"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reprocess}
                  disabled={busy}
                  className="gap-1.5"
                  data-testid="card-asset-reprocess-button"
                >
                  {busy && busyKind === 'reprocess' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
                  Reprocess
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={startUpload}
              disabled={!pending || busy}
              className="gap-1.5"
              data-testid="card-asset-upload-button"
            >
              {busy && busyKind === 'upload' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {hasImage ? 'Replace image' : 'Upload & process'}
            </Button>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-muted-foreground">
          The pipeline runs <span className="text-foreground">rembg</span> (transparent background) and then{' '}
          <span className="text-foreground">GPT-5 Vision</span> for identity / tier / colour / feature extraction. Typical wall time is
          {' '}<span className="font-mono-num">7–15&nbsp;s</span>; the first run downloads the rembg model (~180&nbsp;MB) and may take
          {' '}<span className="font-mono-num">~25&nbsp;s</span>.
        </p>
      </Card>

      {/* Bottom: extracted metadata */}
      {hasImage && (
        <Card className="p-4" data-testid="card-asset-metadata">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <h3 className="font-display text-sm font-semibold">Vision metadata</h3>
              {metadata?.confidence != null && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  Confidence {Math.round((metadata.confidence || 0) * 100)}%
                </Badge>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {image.uploaded_at ? new Date(image.uploaded_at).toLocaleString() : ''}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Identity</div>
              <MetadataRow label="Bank"          value={metadata?.identity?.bank} />
              <MetadataRow label="Product"       value={metadata?.identity?.product_name} />
              <MetadataRow label="Network"       value={metadata?.identity?.network} mono />
              <MetadataRow label="Co-brand"      value={metadata?.identity?.co_brand} />
              <MetadataRow label="Tier"          value={metadata?.tier} />
            </div>

            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Features</div>
              <MetadataRow label="Metal card"     value={metadata?.features?.is_metal === null ? '—' : metadata?.features?.is_metal ? 'Yes' : 'No'} />
              <MetadataRow label="EMV chip"       value={metadata?.features?.has_chip === null ? '—' : metadata?.features?.has_chip ? 'Yes' : 'No'} />
              <MetadataRow label="Contactless"    value={metadata?.features?.has_contactless === null ? '—' : metadata?.features?.has_contactless ? 'Yes' : 'No'} />
              <MetadataRow label="Vertical"       value={metadata?.features?.is_vertical === null ? '—' : metadata?.features?.is_vertical ? 'Yes' : 'No'} />
              <MetadataRow label="Finish"         value={metadata?.visual_tokens?.finish} />
              <MetadataRow label="Body is dark"   value={metadata?.visual_tokens?.is_dark === null ? '—' : metadata?.visual_tokens?.is_dark ? 'Yes' : 'No'} />
            </div>
          </div>

          <Separator className="my-3" />

          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Colour tokens</div>
            <div className="flex items-start gap-5">
              <Swatch hex={metadata?.visual_tokens?.primary_color} label="Primary" />
              <Swatch hex={metadata?.visual_tokens?.accent_color}  label="Accent" />
              <Swatch hex={metadata?.visual_tokens?.text_color}    label="Text" />
            </div>
          </div>

          {image?.original?.filename && (
            <>
              <Separator className="my-3" />
              <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="flex items-center gap-1"><ImageIcon className="h-3 w-3" />{image.original.filename}</span>
                <span>{(image.original.bytes / 1024).toFixed(1)} KB</span>
                <span>{image.original.content_type}</span>
                {image.dimensions?.normalised && <span>{image.dimensions.normalised.join('×')} px</span>}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default CardAssetPanel;
